/**
 * Sandbox Console Filter - Reduces noise from iframe postMessage errors in Lovable editor
 */

const FILTERED_ERROR_PATTERNS = [
  // Lovable.js specific errors - these are the main culprits
  /lovable\.js:1/,
  /at lovable\.js/,
  
  // PostMessage errors (all variants)
  /Failed to execute 'postMessage' on 'DOMWindow'/,
  /postMessage.*origin/i,
  /postMessage.*target/i,
  /postMessage/i,
  
  // Google Maps / Places API warnings
  /PlaceAutocompleteElement is recommended/,
  /deprecated/i,
  
  // Unrecognized browser features
  /Unrecognized feature:/,
  
  // Permissions and iframe security
  /Permissions policy violation/,
  /allowfullscreen attribute/,
  
  // Browser spam
  /ResizeObserver loop limit exceeded/,
  /Non-passive event listener/,
  
  // Generic patterns that catch iframe noise
  /iframe.*cross-origin/i,
  /sandbox.*violation/i,
  /origin.*mismatch/i
];

const FILTERED_WARN_PATTERNS = [
  // Same patterns for warnings
  /lovable\.js:1/,
  /at lovable\.js/,
  /postMessage/i,
  /PlaceAutocompleteElement is recommended/,
  /deprecated/i,
  /Unrecognized feature:/,
  /Permissions policy/,
  /ResizeObserver/,
  /Non-passive event/,
  /iframe/i,
  /cross-origin/i,
  /allowfullscreen/,
  /sandbox/i
];

let originalConsoleError: typeof console.error;
let originalConsoleWarn: typeof console.warn;
let isFilterActive = false;

const shouldFilterError = (message: string): boolean => {
  return FILTERED_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

const shouldFilterWarn = (message: string): boolean => {
  return FILTERED_WARN_PATTERNS.some(pattern => pattern.test(message));
};

export const activateConsoleFilter = () => {
  if (isFilterActive) return;

  // Store original console methods
  originalConsoleError = console.error;
  originalConsoleWarn = console.warn;

  // Override console.error with enhanced filtering
  console.error = (...args: any[]) => {
    const message = args.join(' ');
    const isFiltered = shouldFilterError(message);
    
    if (!isFiltered) {
      originalConsoleError.apply(console, args);
    } else {
      // Debug logging to verify filter is working
      if (localStorage.getItem('LOG_LEVEL') === 'debug') {
        originalConsoleError('🔇 FILTERED ERROR:', message.substring(0, 100) + '...');
      }
    }
  };

  // Override console.warn with enhanced filtering
  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    const isFiltered = shouldFilterWarn(message);
    
    if (!isFiltered) {
      originalConsoleWarn.apply(console, args);
    } else {
      // Debug logging to verify filter is working
      if (localStorage.getItem('LOG_LEVEL') === 'debug') {
        originalConsoleWarn('🔇 FILTERED WARN:', message.substring(0, 100) + '...');
      }
    }
  };

  // Filter window error events more aggressively
  window.addEventListener('error', (event) => {
    const shouldFilter = shouldFilterError(event.message) || 
                        event.filename?.includes('lovable.js') ||
                        event.message?.includes('postMessage');
    
    if (shouldFilter) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);

  // Also filter unhandledrejection events
  window.addEventListener('unhandledrejection', (event) => {
    const reason = String(event.reason);
    if (shouldFilterError(reason)) {
      event.preventDefault();
    }
  }, true);

  isFilterActive = true;
  console.log('🔇 Enhanced console filter activated - lovable.js and iframe spam will be silenced');
};

export const deactivateConsoleFilter = () => {
  if (!isFilterActive) return;

  // Restore original console methods
  console.error = originalConsoleError;
  console.warn = originalConsoleWarn;
  
  isFilterActive = false;
  console.log('🔊 Sandbox console filter deactivated');
};

// Minimal, säker filtrering av känt editor-brus i dev + iframe.
// Filtrerar *bara* vissa meddelanden; rör inte window.onerror/unhandledrejection.
export function installLovableNoiseFilter() {
  // Only in development mode - check Vite env vars safely
  try {
    const isDev = import.meta.env?.DEV;
    if (!isDev) return;
  } catch (e) {
    // Fallback: assume dev if we can't check
    console.debug('Could not check dev mode, assuming development');
  }

  // Endast om vi verkligen kör inuti en iframe (Lovable-editor)
  const inIframe = window.top !== window;
  if (!inIframe) return;

  const isNoisy = (args: any[]) => {
    const msg = String(args?.[0] ?? "");
    // Kända harmlösa mönster från editor-infrastruktur
    if (/postMessage/i.test(msg)) return true;
    if (/Unrecognized feature:\s*(vr|battery)/i.test(msg)) return true;
    return false;
  };

  (["log", "warn", "error", "info"] as const).forEach((method) => {
    const orig = console[method].bind(console);
    console[method] = (...a: any[]) => (isNoisy(a) ? void 0 : orig(...a));
  });

  // Valfritt: markera att filtret är aktivt
  console.debug("🔇 Lovable noise filter activated - iframe editor spam suppressed");
}
